import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Group, Badge, Button, Table, Tabs, Loader } from '@mantine/core'
import { toast } from 'react-toastify'
import { useStore } from '../../app/stores/store'
import { Page, SidebarLayout, EmptyState } from '../../app/common/ui'
import FlagList from './FlagList'
import FlagPreviewPane from './FlagPreviewPane'
import ModerationLogTab from './ModerationLogTab'
import ModeratorSummaryTab from './ModeratorSummaryTab'
import EscalationsTab from './EscalationsTab'
import { TagCategory } from '../../app/models/tag'

const TAG_CATEGORY_LABEL: Record<number, string> = {
  [TagCategory.Platform]: 'Platform',
  [TagCategory.Caliber]: 'Caliber',
  [TagCategory.PartCategory]: 'Part category',
  [TagCategory.ReviewSentiment]: 'Review sentiment',
  [TagCategory.ContentTopic]: 'Content topic',
  [TagCategory.Internal]: 'Internal'
}

export default observer(function ModerationDashboard () {
  const history = useHistory()
  const { userStore, moderationStore, tagStore } = useStore()
  const [activeTab, setActiveTab] = useState<string | null>('flags')

  useEffect(() => {
    if (!userStore.user?.isModerator && !userStore.user?.isAdmin) {
      history.replace('/not-found')
    }
  }, [userStore.user?.isModerator, userStore.user?.isAdmin, history])

  useEffect(() => {
    if (activeTab === 'flags' && (userStore.user?.isModerator || userStore.user?.isAdmin)) {
      moderationStore.loadFlags()
    }
  }, [activeTab, userStore.user?.isModerator, userStore.user?.isAdmin])

  useEffect(() => {
    if (activeTab === 'audit-log' && (userStore.user?.isModerator || userStore.user?.isAdmin)) {
      if (moderationStore.moderationLog === null && !moderationStore.loadingLog) {
        moderationStore.loadModerationLog()
      }
    }
  }, [activeTab, userStore.user?.isModerator, userStore.user?.isAdmin, moderationStore.moderationLog, moderationStore.loadingLog])

  useEffect(() => {
    if (activeTab === 'moderators' && (userStore.user?.isModerator || userStore.user?.isAdmin)) {
      moderationStore.loadModeratorSummary()
    }
  }, [activeTab, userStore.user?.isModerator, userStore.user?.isAdmin])

  useEffect(() => {
    if (activeTab === 'escalations' && (userStore.user?.isModerator || userStore.user?.isAdmin)) {
      moderationStore.loadEscalations()
    }
  }, [activeTab, userStore.user?.isModerator, userStore.user?.isAdmin])

  const loadPendingTags = () => {
    tagStore.loadPendingTags()
  }

  const handleRefresh = () => {
    moderationStore.loadFlags()
  }

  if (!userStore.user?.isModerator && !userStore.user?.isAdmin) return null

  return (
    <Page
      title="Moderation"
      actions={
        <Button variant="subtle" size="sm" onClick={handleRefresh}>
          Refresh
        </Button>
      }
    >
      <Tabs
        value={activeTab ?? 'flags'}
        onTabChange={(v) => {
          setActiveTab(v)
          if (v === 'pending-tags') loadPendingTags()
        }}
      >
        <Tabs.List>
          <Tabs.Tab value="flags">Flag queue</Tabs.Tab>
          <Tabs.Tab value="pending-tags">Pending tags</Tabs.Tab>
          <Tabs.Tab value="audit-log">Audit Log</Tabs.Tab>
          {userStore.user?.isAdmin && <Tabs.Tab value="moderators">Moderators</Tabs.Tab>}
          {userStore.user?.isAdmin && (
            <Tabs.Tab value="escalations">
              <Group spacing={6}>
                Escalations
                {moderationStore.escalations.length > 0 && (
                  <Badge size="sm" color="red">
                    {moderationStore.escalations.length}
                  </Badge>
                )}
              </Group>
            </Tabs.Tab>
          )}
        </Tabs.List>

        <Tabs.Panel value="flags" pt="md">
          <SidebarLayout
            main={<FlagList />}
            sidebar={<FlagPreviewPane />}
            sidebarWidth={440}
          />
        </Tabs.Panel>

        <Tabs.Panel value="pending-tags" pt="md">
          {tagStore.loading && tagStore.pendingTags.length === 0 ? (
            <Loader />
          ) : tagStore.pendingTags.length === 0 ? (
            <EmptyState
              title="No pending tags"
              message="All submitted tags have been reviewed."
            />
          ) : (
            <Table striped>
              <thead>
                <tr>
                  <th>Tag name</th>
                  <th>Display name</th>
                  <th>Category</th>
                  <th>Usage count</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tagStore.pendingTags.map((tag) => (
                  <tr key={tag.id}>
                    <td>{tag.name}</td>
                    <td>{tag.displayName || tag.name}</td>
                    <td>{TAG_CATEGORY_LABEL[tag.category] ?? tag.category}</td>
                    <td>{tag.usageCount}</td>
                    <td>
                      <Group spacing="xs">
                        <Button
                          size="xs"
                          color="green"
                          onClick={async () => {
                            try {
                              await tagStore.approveTag(tag.id)
                              toast.success('Tag approved')
                              loadPendingTags()
                            } catch {
                              // interceptor
                            }
                          }}
                        >
                          Approve
                        </Button>
                        <Button
                          size="xs"
                          color="red"
                          variant="light"
                          onClick={async () => {
                            try {
                              await tagStore.rejectTag(tag.id)
                              toast.success('Tag rejected')
                              loadPendingTags()
                            } catch {
                              // interceptor
                            }
                          }}
                        >
                          Reject
                        </Button>
                      </Group>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="audit-log" pt="md">
          <ModerationLogTab />
        </Tabs.Panel>

        <Tabs.Panel value="moderators" pt="md">
          <ModeratorSummaryTab />
        </Tabs.Panel>

        <Tabs.Panel value="escalations" pt="md">
          <EscalationsTab />
        </Tabs.Panel>
      </Tabs>
    </Page>
  )
})
